import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { ConstraintsChevronIcon } from "shared/assets/svg/ConstraintsChevronIcon";
import { ScrollArea } from "shared/components/ui/scroll-area";
import { useToast } from "shared/components/ui/use-toast";
import appStatusStore from "shared/store/appStatusStore";
import activityPanelStore from "shared/store/activityPanelStore";
import constraintsProductMicrocatsStore from "shared/store/constraintsProductsMicrocatsStore";
import constraintsStore from "shared/store/constraintsStore";
import constraintProfilesStore from "shared/store/constraintProfilesStore";
import { sectionHasAgentChange } from "./changeTracking";
import { sectionGroups, visibleConstraintSectionTypes } from "./panelSections";
import ConstraintsConfig from "config/constraints-config.json";
import { HeaderPanel } from "../HeaderPanel";
import { AssetContent } from "./components/content/contentComponents/AssetContent";
import { CostContent } from "./components/content/contentComponents/CostContent";
import { CurrencyExposureContent } from "./components/content/contentComponents/CurrencyExposureContent";
import { DividendContent } from "./components/content/contentComponents/DividendContent";
import { EsgContent } from "./components/content/contentComponents/EsgContent";
import { IndicatorContent } from "./components/content/contentComponents/IndicatorContent";
import { MacroAssetAllocationContent } from "./components/content/contentComponents/MacroAssetAllocationContent";
import { MacroContent } from "./components/content/contentComponents/MacroContent";
import { MicroCategoryContent } from "./components/content/contentComponents/MicroCategoryContent";
import { ProductContent } from "./components/content/contentComponents/ProductContent";
import { RiskContent } from "./components/content/contentComponents/RiskContent";
import { TopQualityContent } from "./components/content/contentComponents/TopQualityContent";
import { WrapperContent } from "./components/content/contentComponents/WrapperContent";
import { LiquidityContent } from "./components/content/contentComponents/LiquidityContent";
import { ConstraintContent } from "./components/content/ConstraintContent";
import { RedDotIcon } from "shared/assets/svg/menu-icons/RedDotIcon";
import { ConstraintsActionButtons } from "./components/ConstraintsActionButtons";
import { useConstraintsPanel } from "./hooks/useConstraintsPanel";
import { CONSTRAINTS } from "./constants";
import { ConstraintProfileDropdown } from "./components/savedProfiles/ConstraintProfileDropdown";
import { SaveProfileDialog } from "./components/savedProfiles/SaveProfileDialog";
import { ManageProfilesDialog } from "./components/savedProfiles/ManageProfilesDialog";

import styles from "./constraintsPanel.module.scss";

type Props = {
  expanded: boolean;
  onClose: () => void;
  opacityDuration?: number;
  heightOpenDuration?: number;
};

export const ConstraintsPanel: React.FC<Props> = ({
  expanded,
  onClose,
  opacityDuration = 400,
  heightOpenDuration = 400,
}) => {
  const {
    openedConstraints,
    enableSubmitButton,
    contentRefs,
    handleReset,
    handleSubmit,
    toggleConstraint,
    runInProgress,
  } = useConstraintsPanel();

  const { productsSearchResults, microcategoriesSearchResults } =
    constraintsProductMicrocatsStore();

  const constraintsPanelMode = activityPanelStore((state) => state.constraintsPanelMode);

  const agentChangedPaths = constraintsStore((s) => s.agentChangedPaths);
  const acknowledgedSectionGroups = constraintsStore((s) => s.acknowledgedSectionGroups);
  const isModified = constraintsStore((s) => s.isModified());

  const saveDialogOpen = constraintProfilesStore((s) => s.saveDialogOpen);
  const manageDialogOpen = constraintProfilesStore((s) => s.manageDialogOpen);
  const setSaveDialogOpen = constraintProfilesStore((s) => s.setSaveDialogOpen);
  const setManageDialogOpen = constraintProfilesStore((s) => s.setManageDialogOpen);

  // Disable Salva until an actual change is made — for every configuration,
  // Predefiniti included. `isModified()` already diffs the working document
  // against the correct baseline (the applied profile's snapshot, or the
  // `predefiniti` document when Predefiniti is active), so this single check
  // is uniform. (Previously the `&& appliedProfile !== null` clause forced the
  // button always-enabled under Predefiniti, where `appliedProfile === null`.)
  const saveDisabled = !isModified;

  const { agentStatus, setAppStatus } = appStatusStore();
  const { toast } = useToast();
  const { t } = useTranslation();

  const renderContent = (constrainObject: any) => {
    const { type, data } = constrainObject;

    // Filter asset fields in rebalancing mode
    // Hide: Numero strumenti, Peso strumenti, Lotto minimo (Obbligazioni), Lotto minimo (Fondi)
    let filteredData = data;
    if (type === 'asset' && constraintsPanelMode !== 'builder') {
      const { asset_count_min, asset_count_max, asset_weight_min, asset_weight_max, investment_bonds_max, investment_funds_max, ...rest } = data;
      filteredData = rest;
    }

    const props = {
      configData: filteredData,
    };

    const components = {
      risk: () => <RiskContent {...props} />,
      esg: () => <EsgContent {...props} />,
      dividend: () => <DividendContent {...props} />,
      cost: () => <CostContent {...props} />,
      asset: () => <AssetContent {...props} />,
      indicator: () => <IndicatorContent {...props} />,
      top_quality: () => <TopQualityContent {...props} />,
      wrapper: () => <WrapperContent {...props} />,
      macro_asset_allocation: () => <MacroAssetAllocationContent {...props} />,
      currency_exposure: () => <CurrencyExposureContent {...props} />,
      macro: () => <MacroContent {...props} />,
      product: () => <ProductContent {...props} />,
      microcategoria_advice: () => <MicroCategoryContent {...props} />,
      order: () => <ConstraintContent {...props} dataKey="order" />,
      liquidity: () => <LiquidityContent {...props} />,
    };

    return components[type]?.() || null;
  };

  useEffect(() => {
    setTimeout(() => {
      ConstraintsConfig.constraints.forEach((constraintConfigObject) => {
        const el = contentRefs.current[constraintConfigObject.type];
        if (el) {
          el.style.maxHeight = openedConstraints.includes(
            constraintConfigObject.type,
          )
            ? `${el.scrollHeight + 0}px`
            : "0px";
        }
      });
    }, 50);
  }, [
    openedConstraints,
    productsSearchResults,
    microcategoriesSearchResults,
  ]);

  const children = useMemo(() => {
    // Filter constraints based on mode (shared with the menu-button red-dot
    // derivation in MenuList.tsx via `visibleConstraintSectionTypes`).
    const visibleTypes = new Set(visibleConstraintSectionTypes(constraintsPanelMode));
    const filteredConstraints = ConstraintsConfig.constraints.filter((constraint) =>
      visibleTypes.has(constraint.type),
    );

    return (
      <div className={styles.constraintsContainer}>
        <div className={styles.constraintsInnerContainer}>
          {filteredConstraints.map((constraintConfigObject, index) => {
              let opened = false;
              if (openedConstraints.includes(constraintConfigObject.type)) {
                opened = true;
              }

              return (
                <div key={index}>
                  <div
                    className={[
                      styles.constraintButtonContainer,
                      index < filteredConstraints.length - 1
                        ? styles.constraintButtonContainerBorder
                        : null,
                    ].join(" ")}
                  >
                    <div
                      className="flex w-full cursor-pointer flex-row items-center justify-between "
                      style={{
                        paddingTop: 24,
                        paddingBottom: 24,
                      }}
                      onClick={() => {
                        toggleConstraint(constraintConfigObject.type);
                        constraintsStore.getState().acknowledgeSection(sectionGroups(constraintConfigObject.type));
                      }}
                    >
                      <div className={styles.constraintSectionTitleContainer}>
                        <div className={styles.constraintSectionTitle}>
                          {constraintConfigObject.label}
                        </div>

                        {sectionHasAgentChange(constraintConfigObject.type, agentChangedPaths, acknowledgedSectionGroups) && (
                            <div style={{ marginLeft: 5 }}>
                              <RedDotIcon small={true} />
                            </div>
                          )}
                        <ConstraintsChevronIcon
                          className={styles.constraintSectionChevron}
                          isExpanded={opened}
                        />
                      </div>
                    </div>

                    <div
                      ref={(el) =>
                        (contentRefs.current[constraintConfigObject.type] = el)
                      }
                      style={{
                        maxHeight: opened
                          ? `${contentRefs.current[constraintConfigObject.type]?.scrollHeight || 0}px`
                          : "0px",
                        transition: opened
                          ? `max-height ${heightOpenDuration}ms ease, opacity ${opacityDuration}ms ease`
                          : "none",
                        opacity: opened ? 1 : 0,
                      }}
                    >
                      <div
                        className=""
                        style={{
                          marginBottom: opened ? 24 : 0,
                          transition: opened
                            ? "margin-bottom 600ms ease-in-out 250ms"
                            : "none",
                        }}
                      >
                        {opened && renderContent(constraintConfigObject)}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    );
  }, [openedConstraints, agentChangedPaths, acknowledgedSectionGroups, constraintsPanelMode]);

  return (
    <div className={styles.panelContainer}>
      {(agentStatus === "working" || runInProgress) && (
        <div
          className={styles.busyOverlay}
          onClick={() => {
            toast({
              title: t(CONSTRAINTS.BUSY_OVERLAY.TITLE_KEY),
              description: t(CONSTRAINTS.BUSY_OVERLAY.DESCRIPTION_KEY),
              variant: "destructive",
            });
            setAppStatus("idle");
          }}
        />
      )}

      <div 
        className={styles.panelContainerInner}
        style={{
          pointerEvents: runInProgress ? 'none' : 'auto',
          opacity: runInProgress ? 0.6 : 1
        }}
      >
        <HeaderPanel
          expanded={expanded}
          onClose={onClose}
          title={"menu.constraints"}
          extra={<ConstraintProfileDropdown />}
        />
        <ScrollArea className={styles.scrollArea}>
          {expanded && <div className={styles.innerScrollArea}>{children}</div>}
        </ScrollArea>
        <div className={styles.constraintNotes}>
          <div
            style={{
              textAlign: "left",
              width: "100%",
            }}
          >
            {t("constraints.pianofinanziario")}
          </div>
          <div
            style={{
              textAlign: "right",
              width: "100%",
            }}
          >
            {t("constraints.portafoglioGenerato")}
          </div>
        </div>
        <ConstraintsActionButtons
          onSubmit={handleSubmit}
          onReset={handleReset}
          onSave={() => constraintProfilesStore.getState().setSaveDialogOpen(true)}
          saveDisabled={saveDisabled}
          enableSubmitButton={enableSubmitButton}
          runInProgress={runInProgress}
        />
      </div>
      <SaveProfileDialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen} />
      <ManageProfilesDialog open={manageDialogOpen} onOpenChange={setManageDialogOpen} />
    </div>
  );
};
